// ABOUTME: The runner's v28 console card: tracker, phase line, headline, error and notice regions, and the action buttons drawn from progress.actions.
// ABOUTME: Buttons are aria-disabled (never disabled) while saving; Exit-slot actions go to the frame and are never sent.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import type { CrossingProgress, CrossingProgressAction } from '../../../types/api';
import {
  CROSSING_BORDER_PILL, CROSSING_CARD_SHADOW, CROSSING_FOCUS_RING, CROSSING_GOLD_BTN, CROSSING_GOLD_BTN_HOVER, CROSSING_GOLD_V28,
  CROSSING_HEADLINE_INK, CROSSING_INK_MUTED, CROSSING_INK_STRONG, CROSSING_PAPER, CROSSING_PAPER_94, CROSSING_PILL_SHADOW, CROSSING_PLATE_BG, CROSSING_PLATE_BORDER, CROSSING_SUB_INK, CROSSING_TEAL_BAR, CROSSING_TEAL_TEXT_ON,
} from '../../../theme/crossingTokens';
import Tracker from '../crossing/Tracker';
import { phaseLine } from './phaseLine';
import { useCrossingReducedMotion } from '../crossing/useReducedMotion';
import { usePhoneLayout } from './usePhoneLayout';
import { visuallyHidden } from './visuallyHidden';
import { RUNNER_PLATE_BOTTOM_PX, RUNNER_PLATE_FIRST_SCREEN_BOTTOM_PX } from './plateClearance';
import type { RunnerError, RunnerStatus } from './useCrossingRunner';
import type { ScreenResult } from './screenContract';

const SPINNER_DELAY_MS = 400;
const SLOT_ORDER: Record<string, number> = { primary: 0, secondary: 1, next: 2, exit: 3 };

const RUN_ANOTHER_ACTION_ID = 'run_another';
// Drawn before the run_another label; client-side only, the definition's label does not change.
const RUN_ANOTHER_PREFIX = '↺ ';

type PillStyle = 'primary' | 'secondary' | 'dashed';

// `backwards` holds the first keyframe through the delay and nothing after the fade, so the button's own style (the aria-disabled dim) is what it rests at.
// visibility keeps its place in the row while it cannot be focused, read or tapped.
const FADE_IN_SX = {
  '@keyframes ckPlateButtonIn': { from: { opacity: 0, visibility: 'hidden' } },
  animation: 'ckPlateButtonIn 0.5s ease 2.1s backwards',
} as const;

// The v28 pill buttons. MUI's Button stays underneath so aria-disabled, data-* and onClick behave as before.
function pillSx(style: PillStyle, fadesIn = false) {
  const shared = {
    textTransform: 'none', borderRadius: '999px', fontSize: '13.5px', fontWeight: 800, lineHeight: 1.3, padding: '11.5px 20.5px',
    '&[aria-disabled="true"]': { opacity: 0.55, cursor: 'default' },
    '&.Mui-focusVisible, &:focus-visible': { outline: `2px solid ${CROSSING_FOCUS_RING}`, outlineOffset: 2 },
    ...(fadesIn ? FADE_IN_SX : {}),
  } as const;
  if (style === 'primary') {
    return {
      ...shared, backgroundColor: CROSSING_TEAL_BAR, color: CROSSING_TEAL_TEXT_ON, border: '1px solid transparent', boxShadow: CROSSING_PILL_SHADOW,
      '&:hover': { backgroundColor: CROSSING_HEADLINE_INK },
    };
  }
  return {
    ...shared, backgroundColor: CROSSING_PAPER_94, border: `1px ${style === 'dashed' ? 'dashed' : 'solid'} ${CROSSING_BORDER_PILL}`,
    color: style === 'dashed' ? CROSSING_SUB_INK : CROSSING_INK_MUTED, boxShadow: 'none',
    '&:hover': { backgroundColor: CROSSING_PAPER_94 },
  };
}

// The label on the Reload button the plate shows after a client error.
const RELOAD_LABEL = 'Reload';

interface RunnerPlateProps {
  progress: CrossingProgress;
  result: ScreenResult;
  headlineId: string;
  status: RunnerStatus;
  error: RunnerError | null;
  notice: string | null;
  announcement: string | null;
  pressedActionId: string | null;
  onAction: (actionId: string, values?: Record<string, unknown>) => void;
  onExit: (name: string) => void;
  onReload: () => void;
  // The history arrows, drawn by the frame beside the tracker bars.
  backArrow?: ReactNode;
  forwardArrow?: ReactNode;
  inert?: boolean; // set while an overlay page covers the plate (a screen that dims its plate is inert too)
  onHeight?: (heightPx: number) => void; // told the plate's height as it changes, so the scene can keep its content above it
}

const pickerArrowSx = {
  width: 36, height: 36, minWidth: 36, padding: 0, flexShrink: 0, borderRadius: '50%', backgroundColor: CROSSING_GOLD_BTN, color: CROSSING_INK_STRONG,
  fontSize: '22px', lineHeight: 1,
  '&:hover': { backgroundColor: CROSSING_GOLD_BTN_HOVER },
  '&.Mui-focusVisible, &:focus-visible': { outline: `2px solid ${CROSSING_FOCUS_RING}`, outlineOffset: 2 },
} as const;

function PickerRow({ picker }: { picker: NonNullable<ScreenResult['picker']> }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
      <IconButton aria-label={picker.prevLabel} onClick={picker.onPrev} sx={pickerArrowSx}>&#8249;</IconButton>
      <Box
        data-testid="picker-label"
        sx={{
          minWidth: 218, boxSizing: 'border-box', padding: '10px 26px', borderRadius: '20px', backgroundColor: CROSSING_PAPER,
          border: `1.5px solid ${CROSSING_GOLD_V28}`, fontSize: '19px', fontWeight: 800, color: CROSSING_INK_STRONG,
        }}
      >
        {picker.label}
      </Box>
      <IconButton aria-label={picker.nextLabel} onClick={picker.onNext} sx={pickerArrowSx}>&#8250;</IconButton>
    </Box>
  );
}

function InlineProgress() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), SPINNER_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  return visible ? <CircularProgress size={14} color="inherit" sx={{ ml: 1 }} /> : null;
}

// Primary and next slots and the 5D `done` exit are primary pills; run_another is dashed; everything else is secondary.
function pillStyleFor(action: CrossingProgressAction): PillStyle {
  if (action.id === RUN_ANOTHER_ACTION_ID) return 'dashed';
  if (action.slot === 'primary' || action.slot === 'next' || action.id === 'done') return 'primary';
  return 'secondary';
}

function exitName(action: CrossingProgressAction): string {
  return 'exit' in action.target ? action.target.exit : action.id;
}

export default function RunnerPlate({
  progress, result, headlineId, status, error, notice, announcement, pressedActionId, onAction, onExit, onReload, backArrow, forwardArrow, inert, onHeight,
}: RunnerPlateProps) {
  const plateRef = useRef<HTMLDivElement>(null);
  const phone = usePhoneLayout();
  const reducedMotion = useCrossingReducedMotion();
  useEffect(() => {
    const plate = plateRef.current;
    if (!plate || !onHeight || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => onHeight(plate.getBoundingClientRect().height));
    observer.observe(plate);
    return () => observer.disconnect();
  }, [onHeight]);

  const saving = status === 'saving';
  // An overlay page owns its chrome, primary action included, so the covered plate draws none: exactly one element
  // answers to the action id. A screen can also hide a slot it draws itself or that does not apply.
  const drawn = progress.actions
    .filter(action => action.slot !== 'in_screen'
      && !(progress.kind === 'overlay_page' && action.slot === 'primary')
      && !result.slots?.[action.id]?.hidden)
    .sort((a, b) => SLOT_ORDER[a.slot] - SLOT_ORDER[b.slot]);

  const press = (action: CrossingProgressAction) => {
    const override = result.slots?.[action.id];
    if (saving || override?.disabled || (progress.closed && action.slot !== 'exit')) return;
    if (action.slot === 'exit') {
      onExit(exitName(action));
      return;
    }
    if (override?.onPress) {
      override.onPress();
      return;
    }
    onAction(action.id, override?.values);
  };

  const { glyph, text } = phaseLine(progress.screenId, progress.eyebrow, progress.phaseNumber, result.phaseVariant);
  const bottom = phone ? 0 : progress.screenId === '1A' ? RUNNER_PLATE_FIRST_SCREEN_BOTTOM_PX : RUNNER_PLATE_BOTTOM_PX;

  return (
    <Box
      ref={plateRef}
      inert={inert || result.plateDimmed}
      sx={{
        position: 'fixed', left: 0, right: 0, bottom, zIndex: 10, display: 'flex', justifyContent: 'center', px: phone ? 0 : 2,
        pointerEvents: 'none', transition: 'bottom 0.5s ease', ...(result.plateDimmed ? { opacity: 0.4 } : {}),
      }}
    >
      <Box
        data-testid="runner-plate"
        sx={{
          pointerEvents: 'auto', width: '100%', boxSizing: 'border-box',
          ...(phone ? { borderRadius: '18px 18px 0 0', minHeight: '230px' } : { maxWidth: 560, borderRadius: '18px' }),
          backgroundColor: CROSSING_PLATE_BG, border: `1px solid ${CROSSING_PLATE_BORDER}`,
          boxShadow: CROSSING_CARD_SHADOW, padding: '13px 28px 18px', display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'center',
        }}
      >
        <Tracker pills={progress.pills} backArrow={backArrow} forwardArrow={forwardArrow} hidden={result.hideRail} />

        <Typography data-testid="runner-phase-line" sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.4px', color: CROSSING_HEADLINE_INK }}>
          <span aria-hidden="true">{glyph}</span>{' '}{text}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <Typography
            id={headlineId}
            component="h1"
            data-headline-hidden={result.headlineHidden || undefined}
            sx={{
              fontFamily: 'Newsreader, serif', fontSize: '21px', fontWeight: 600, lineHeight: 1.25, color: CROSSING_HEADLINE_INK,
              display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden',
              ...(result.headlineHidden ? visuallyHidden : {}),
            }}
          >
            {result.headline}
          </Typography>
          {result.plateLine}
          {result.picker && <PickerRow picker={result.picker} />}
          {result.sub && <Typography sx={{ fontSize: '14px', fontWeight: 600, color: CROSSING_INK_MUTED, lineHeight: 1.4 }}>{result.sub}</Typography>}
          {/* Permanently mounted so a screen reader hears the text appear rather than a new region. */}
          <Box role="alert" data-testid="runner-alert" sx={{ fontSize: '13px', fontWeight: 700, color: CROSSING_HEADLINE_INK }}>
            {error?.message}
          </Box>
          {error?.kind === 'refresh' && (
            <Button variant="text" onClick={onReload} sx={pillSx('secondary')}>{RELOAD_LABEL}</Button>
          )}
          {notice && <Typography data-testid="runner-notice" sx={{ fontSize: '13px', color: CROSSING_SUB_INK }}>{notice}</Typography>}
          <Box role="status" aria-live="polite" aria-atomic="true" data-testid="runner-live-region" sx={visuallyHidden}>
            {announcement}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {result.localActions?.map(local => (
            <Button
              key={local.id}
              data-local-action-id={local.id}
              variant="text"
              aria-disabled={local.disabled || undefined}
              onClick={() => { if (!local.disabled) local.onPress(); }}
              sx={pillSx(local.filled ? 'primary' : 'secondary')}
            >
              {local.glyph && <><span aria-hidden="true">{local.glyph}</span>{' '}</>}
              {local.label}
            </Button>
          ))}
          {drawn.map(action => {
            const override = result.slots?.[action.id];
            const pressed = saving && action.id === pressedActionId;
            // A closed crossing offers exits only; the server already filters, this keeps the plate honest if it does not.
            const unavailable = saving || !!override?.disabled || (progress.closed && action.slot !== 'exit');
            return (
              <Button
                key={action.id}
                data-action-id={action.id}
                variant="text"
                aria-disabled={unavailable || undefined}
                aria-busy={pressed || undefined}
                onClick={() => press(action)}
                sx={pillSx(pillStyleFor(action), !!override?.fadesIn && !reducedMotion)}
              >
                {action.id === RUN_ANOTHER_ACTION_ID ? RUN_ANOTHER_PREFIX : ''}
                {override?.glyph && <><span aria-hidden="true">{override.glyph}</span>{' '}</>}
                {override?.label ?? action.label}
                {pressed && <InlineProgress />}
              </Button>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
